import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Modal,
  Progress,
  Radio,
  Result,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import {
  ApartmentOutlined,
  CaretRightOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

import {
  FlowNode,
  PatientTreatmentPlan,
  PatientTreatmentPlanStageLog,
  PractitionerOption,
  PractitionerTaskType,
  STAGE_CONFIG,
  StageType,
  SubPlanOption,
} from '../../../../../types/healthcare/TreatmentPlanTypes';
import { EvidenceRef } from '../../../../../types/healthcare/PatientAnalysisTypes';
import { getStageIcon } from '../../../../../utils/healthcare/treatmentPlanIcons';
import { EvidenceRefInteractionContext } from '../../../../../components/healthcare/evidenceRefs/EvidenceRefInteractionContext';
import { useReferenceModal } from '../../../../../hooks/xoe-actions/useReferenceModal';
import { buildPerformedActions, PerformedActionVM } from './workflowProgress/stageActivity';
import CoaAnalysisPanel, { CoaAnalysis } from '../CoaAnalysisPanel';
import { isCoaAnalysisWatcher, latestCoaAnalysis, latestCoaLog } from './coaWatcher';
import { completePractitionerTask, reviewPatientAnalysis, selectSubPlans, advanceStage, skipStage, checkBloodResults, fetchPlanStageLog } from '../../../../../api/healthcare/TreatmentPlanApi';
import { assignForm, getPractitionerAssignedForms, getPractitionerFormToRender } from '../../../../../api/formApi';
import { fetchSessionByJobUuid, fetchSessionByJobId } from '../../../../../api/healthcare/Consultation/ConsultApi';
import { useNotifications } from '../../../../../context/NotificationContext';
import ConsultGuidancePanel from '../../../consultation/consultationComponents/ConsultGuidancePanel';
import ComprehensiveAnalysisRenderer from './workflowProgress/ComprehensiveAnalysisRenderer';
import ComprehensiveAnalysisInputRenderer from './ComprehensiveAnalysisInputRenderer';

import UploadResourceTaskForm from './taskForms/UploadResourceTaskForm';
import CompletePractitionerFormTaskForm from './taskForms/CompletePractitionerFormTaskForm';
import CreateTestOrderTaskForm from './taskForms/CreateTestOrderTaskForm';
import DocumentMedicalHistoryTaskForm from './taskForms/DocumentMedicalHistoryTaskForm';
import RecordVitalSignsTaskForm from './taskForms/RecordVitalSignsTaskForm';
import CompleteReferralTaskForm from './taskForms/CompleteReferralTaskForm';
import PrepareMedicationTaskForm from './taskForms/PrepareMedicationTaskForm';
import SelectDispensingPharmacyTaskForm from './taskForms/SelectDispensingPharmacyTaskForm';
import { isFormDone } from '../../../../../utils/forms/formStatus';
import type { UserUuid } from '../../../../../types/identity';

const { Text } = Typography;

const SECTION_LABEL_STYLE: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'var(--mg-text-secondary)',
  marginBottom: 4,
};

// A completed or skipped stage is "done" — its action controls (skip / complete / advance) are no
// longer relevant and must not be shown when the practitioner revisits it from the stage list.
const isStageComplete = (log: PatientTreatmentPlanStageLog): boolean =>
  log.status === 'completed' || log.status === 'skipped';

// Shown in place of a stage's action footer once the stage is done, so the content stays visible
// but no longer offers stale actions. Reflects whether the stage was completed or skipped.
const StageStatusNote: React.FC<{ log: PatientTreatmentPlanStageLog; noun?: string }> = ({ log, noun = 'Step' }) => {
  const skipped = log.status === 'skipped';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 6, background: skipped ? 'var(--mg-bg-subtle)' : 'var(--mg-success-bg)', border: `1px solid ${skipped ? 'var(--mg-border)' : 'var(--mg-success)'}` }}>
      {skipped
        ? <MinusCircleOutlined style={{ color: 'var(--mg-text-disabled)', fontSize: 14 }} />
        : <CheckCircleOutlined style={{ color: 'var(--mg-success)', fontSize: 14 }} />}
      <Text style={{ fontSize: 12, color: skipped ? 'var(--mg-text-secondary)' : 'var(--mg-success)', fontWeight: 500 }}>
        {noun} {skipped ? 'skipped' : 'completed'}
      </Text>
    </div>
  );
};

// Uploaded certificate file ids for the CoA-analysis watcher views, read from the SAME
// stage-log the analysis came from. Delegates to coaWatcher.ts's latestCoaLog — the ONE place
// the log-selection rule lives — so a retry attempt's document pane shows its own upload, never
// a prior attempt's, and any future change to the selection rule can't silently diverge between
// the analysis and its file ids.
const coaFileIdsFromLatestUpload = (
  stageLogs: PatientTreatmentPlanStageLog[],
  currentIteration?: number,
): string[] | undefined => {
  const log = latestCoaLog(stageLogs, currentIteration);
  return (log?.outcome_data as any)?.task_result?.uploaded_file_ids as string[] | undefined;
};

// ── Time-delay (time_condition) renderer ────────────────────────────────────
// A time_condition is an automatic stage the plan SITS on until its scheduled time
// (outcome_data.fire_at). Unlike trigger/condition — which pass through instantly — it
// genuinely pauses the plan for days, so the practitioner needs to see that the plan is
// waiting and when the next step will run automatically.
const TimeDelayStatusRenderer: React.FC<{ log: PatientTreatmentPlanStageLog }> = ({ log }) => {
  if (isStageComplete(log)) {
    return <StageStatusNote log={log} noun="Time delay" />;
  }

  const fireAtRaw = log.outcome_data?.fire_at as string | undefined;
  const fireAt = fireAtRaw ? dayjs(fireAtRaw) : null;
  const title = log.stage_snapshot?.title;
  const accent = {
    border: 'var(--mg-warning-border, #faad14)',
    bg: 'var(--mg-warning-bg, #fffbe6)',
    text: 'var(--mg-warning-text, #ad6800)',
  };

  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: 6,
        border: `1px solid ${accent.border}`,
        background: accent.bg,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <ClockCircleOutlined style={{ color: accent.text, fontSize: 13 }} />
        <Text style={{ fontSize: 12, fontWeight: 600, color: accent.text }}>
          Waiting at a time delay
        </Text>
      </div>

      {title && <Text style={{ fontSize: 12, color: 'var(--mg-text)' }}>{title}</Text>}

      <Text style={{ fontSize: 12, color: 'var(--mg-text-secondary)' }}>
        {fireAt ? (
          <>
            The next step runs automatically on{' '}
            <Text strong style={{ fontSize: 12 }}>{fireAt.format('D MMM YYYY, h:mm A')}</Text>{' '}
            ({fireAt.fromNow()}).
          </>
        ) : (
          <>The next step runs automatically once the waiting period has elapsed.</>
        )}
      </Text>

      <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>
        No action is required from you — the plan will continue on its own.
      </Text>
    </div>
  );
};

export interface InlineStageRendererProps {
  plan: PatientTreatmentPlan;
  activeLog: PatientTreatmentPlanStageLog;
  activeNode: FlowNode | undefined;
  /**
   * Watcher visually joined to this stage (frontend merge — see watcherMerge.ts). When its `log`
   * exists, the plan has reached the watcher: the action view shows what was performed plus the
   * watcher's target/state inline instead of the watcher rendering as a standalone node.
   */
  joinedWatcher?: { node?: FlowNode; log?: PatientTreatmentPlanStageLog | null };
  /** Open a patient's submitted form (read-only) — used by a satisfied form_completed watcher. */
  onViewForm?: (formId: number) => void;
  patientId: UserUuid;
  patientUuid: string;
  practitionerId: UserUuid;
  userId: UserUuid;
  onComplete: () => void;
  onPlanChange: () => void;
  /** All stage logs on the plan — used to surface the CoA analysis result (stored on the upload
   *  stage-log) while the downstream coa_analysis_complete watcher is the active stage. */
  stageLogs?: PatientTreatmentPlanStageLog[];
}

// ── Practitioner Task renderer ──────────────────────────────────────────────

interface PractitionerTaskRendererProps {
  plan: PatientTreatmentPlan;
  activeLog: PatientTreatmentPlanStageLog;
  patientId: UserUuid;
  patientUuid: string;
  practitionerId: UserUuid;
  userId: UserUuid;
  onComplete: () => void;
}

const PractitionerTaskRenderer: React.FC<PractitionerTaskRendererProps> = ({
  plan,
  activeLog,
  patientId,
  patientUuid,
  practitionerId,
  userId,
  onComplete,
}) => {
  const outcomeData = activeLog.outcome_data ?? {};
  const taskType = outcomeData.task_type as PractitionerTaskType | undefined;
  const taskTitle = outcomeData.task_title ?? 'Complete Task';

  // A completed certificate-of-analysis upload carries the clinical AI CoA analysis
  // (stored by AnalyseCoaJob on this stage-log) — surface it read-only beneath the task.
  const isCoaUpload =
    taskType === 'upload_resource' &&
    outcomeData.task_result?.document_category === 'certificate-of-analysis';
  const coaAnalysis = outcomeData.ai_coa_analysis as CoaAnalysis | undefined;
  const coaFileIds = outcomeData.task_result?.uploaded_file_ids as string[] | undefined;
  const hasCoaFiles = Array.isArray(coaFileIds) && coaFileIds.length > 0;
  const instructions = outcomeData.task_instructions as string | undefined;
  const taskConfig = outcomeData.task_config ?? {};

  const isCompletedFormTask =
    activeLog.status === 'completed' &&
    taskType === 'complete_practitioner_form';

  const [taskPayload, setTaskPayload] = useState<Record<string, unknown>>({});
  const [isValid, setIsValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [assignedFormId, setAssignedFormId] = useState<number | null>(null);
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, any> | undefined>(
    outcomeData.ai_form_suggestions,
  );
  const [aiSuggestionRefs, setAiSuggestionRefs] = useState<Record<string, EvidenceRef[]> | undefined>(
    outcomeData.ai_form_suggestion_refs,
  );
  const [savedResponses, setSavedResponses] = useState<Record<string, any> | null>(null);
  const [loadingSavedForm, setLoadingSavedForm] = useState(false);

  useEffect(() => {
    if (!isCompletedFormTask) return;
    const savedFormId: number | undefined = outcomeData.task_result?.assigned_form_id;
    if (!savedFormId) return;
    setLoadingSavedForm(true);
    getPractitionerFormToRender(savedFormId)
      .then(({ assigned }) => {
        const raw = assigned.response?.response;
        if (!raw) { setSavedResponses({}); return; }
        try {
          const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
          if (Array.isArray(parsed)) {
            setSavedResponses(
              parsed.reduce((acc: Record<string, any>, f: { id: string; response: any }) => {
                acc[f.id] = f.response;
                return acc;
              }, {}),
            );
          } else {
            setSavedResponses(parsed as Record<string, any>);
          }
        } catch {
          setSavedResponses({});
        }
      })
      .catch(() => setSavedResponses({}))
      .finally(() => setLoadingSavedForm(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-assign form for complete_practitioner_form task type
  useEffect(() => {
    if (taskType !== 'complete_practitioner_form' || !taskConfig.form_id || !patientUuid) return;
    if (isCompletedFormTask) return;
    getPractitionerAssignedForms(practitionerId, patientId)
      .then((forms) => {
        // F6: "not done" is Completed OR Reviewed, not Completed alone. A practitioner
        // form that had been reviewed read as reusable here, so the stage would re-open a
        // finished assignment instead of creating a new one.
        const existing = forms.find(
          (f) =>
            f.form_id === taskConfig.form_id &&
            f.assignee_type === 'practitioner' &&
            !isFormDone(f.status),
        );
        if (existing) {
          setAssignedFormId(existing.id);
          return;
        }
        return assignForm(
          {
            form_id: taskConfig.form_id,
            practitioner_id: practitionerId,
            patient_id: patientId,
            // F6: was lowercase 'pending'. `Rule::enum` is case-sensitive, so this 422s and
            // takes every `complete_practitioner_form` stage with it. No other F6 subtask
            // touches this file — it is the easy one to miss.
            status: 'Pending',
            assignee_type: 'practitioner',
            assigned_to_practitioner_id: practitionerId,
          },
          patientUuid,
        ).then((data: { data?: { id?: number }; id?: number }) => {
          const id = data?.data?.id ?? data?.id ?? null;
          if (id) setAssignedFormId(id);
        });
      })
      .catch(() => {
        // Non-blocking
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (taskType !== 'complete_practitioner_form') return;
    if (isCompletedFormTask) return;
    if (aiSuggestions !== undefined) return;

    let cancelled = false;
    const MAX_POLLS = 30;
    let count = 0;

    const poll = async () => {
      if (cancelled || count >= MAX_POLLS) return;
      count++;
      try {
        const logs = await fetchPlanStageLog(plan.id);
        const log = logs.find((l) => l.id === activeLog.id);
        if (log?.outcome_data?.ai_form_suggestions !== undefined) {
          if (!cancelled) {
            setAiSuggestions(log.outcome_data.ai_form_suggestions);
            setAiSuggestionRefs(log.outcome_data.ai_form_suggestion_refs);
          }
          return;
        }
      } catch {
        // non-blocking
      }
      if (!cancelled) setTimeout(poll, 4000);
    };

    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = useCallback((payload: Record<string, unknown>, valid: boolean) => {
    setTaskPayload(payload);
    setIsValid(valid);
  }, []);

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await completePractitionerTask(plan.id, activeLog.id, practitionerId, taskPayload);
      onComplete();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ??
        (err as { message?: string })?.message ??
        'Failed to complete task.';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const renderTaskForm = () => {
    if (!taskType) {
      return (
        <div
          style={{
            padding: '12px 16px',
            borderRadius: 8,
            background: 'var(--mg-danger-bg)',
            border: '1px solid var(--mg-danger)',
          }}
        >
          <Text style={{ color: 'var(--mg-danger)', fontSize: 12 }}>
            Unknown task type. Please contact support.
          </Text>
        </div>
      );
    }

    switch (taskType) {
      case 'upload_resource':
        return (
          <UploadResourceTaskForm
            taskConfig={taskConfig}
            patientId={patientId}
            userId={userId}
            onChange={handleChange}
          />
        );
      case 'complete_practitioner_form':
        if (isCompletedFormTask) {
          if (loadingSavedForm) return <Spin size="small" />;
          return (
            <CompletePractitionerFormTaskForm
              taskConfig={taskConfig}
              assignedFormId={assignedFormId}
              readOnly
              initialResponses={savedResponses ?? {}}
              onChange={() => {}}
            />
          );
        }
        return (
          <CompletePractitionerFormTaskForm
            taskConfig={taskConfig}
            assignedFormId={assignedFormId}
            aiSuggestions={aiSuggestions}
            aiSuggestionRefs={aiSuggestionRefs}
            onChange={handleChange}
          />
        );
      case 'create_test_order':
        return (
          <CreateTestOrderTaskForm
            taskConfig={taskConfig}
            patientId={patientId}
            onChange={handleChange}
          />
        );
      case 'document_medical_history':
        return (
          <DocumentMedicalHistoryTaskForm
            taskConfig={taskConfig}
            patientId={patientId}
            onChange={handleChange}
          />
        );
      case 'record_vital_signs':
        return <RecordVitalSignsTaskForm taskConfig={taskConfig} onChange={handleChange} />;
      case 'complete_referral':
        return <CompleteReferralTaskForm taskConfig={taskConfig} onChange={handleChange} />;
      case 'prescribe_medication':
        return (
          <PrepareMedicationTaskForm
            taskConfig={taskConfig}
            patientId={patientId}
            onChange={handleChange}
            dispenseDestination={outcomeData.task_result?.dispense_destination}
          />
        );
      case 'select_dispensing_pharmacy':
        return (
          <SelectDispensingPharmacyTaskForm
            taskConfig={taskConfig}
            onChange={handleChange}
          />
        );
      default:
        return (
          <div
            style={{
              padding: '12px 16px',
              borderRadius: 8,
              background: 'var(--mg-danger-bg)',
              border: '1px solid var(--mg-danger)',
            }}
          >
            <Text style={{ color: 'var(--mg-danger)', fontSize: 12 }}>
              Unsupported task type: {taskType}
            </Text>
          </div>
        );
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={SECTION_LABEL_STYLE}>Task</div>
        <Text strong style={{ fontSize: 13 }}>{taskTitle}</Text>
      </div>

      {instructions && (
        <div
          style={{
            padding: '12px 16px',
            borderRadius: 8,
            background: 'var(--mg-info-bg)',
            border: '1px solid var(--mg-info)',
          }}
        >
          <Text style={{ fontSize: 12, lineHeight: 1.6 }}>{instructions}</Text>
        </div>
      )}

      {renderTaskForm()}

      {isCoaUpload && (
        <CoaAnalysisPanel
          analysis={coaAnalysis}
          hasCoaFile={hasCoaFiles}
          fileIds={coaFileIds}
          patientId={patientId}
        />
      )}

      {!isCompletedFormTask && submitError && (
        <Alert
          type="error"
          message={submitError}
          closable
          onClose={() => setSubmitError(null)}
          style={{ fontSize: 12 }}
        />
      )}

      {isStageComplete(activeLog) ? (
        <StageStatusNote log={activeLog} noun="Task" />
      ) : (
        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8 }}>
          <Button
            type="primary"
            loading={submitting}
            disabled={!isValid || submitting}
            onClick={handleSubmit}
            style={{ height: 32, fontSize: 12 }}
          >
            Complete & Advance →
          </Button>
        </div>
      )}
    </div>
  );
};

// ── Patient Analysis renderer ───────────────────────────────────────────────

interface PatientAnalysisRendererProps {
  plan: PatientTreatmentPlan;
  activeLog: PatientTreatmentPlanStageLog;
  practitionerId: UserUuid;
  onComplete: () => void;
}

const PatientAnalysisRenderer: React.FC<PatientAnalysisRendererProps> = ({
  plan,
  activeLog,
  practitionerId,
  onComplete,
}) => {
  const { showError } = useNotifications();
  const jobUuid: string | undefined = activeLog.outcome_data?.job_uuid;
  const consultJobId: number | undefined = activeLog.outcome_data?.consult_job_id;

  const [reviewing, setReviewing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pollError, setPollError] = useState(false);
  const [sessionData, setSessionData] = useState<{ status: string; session: any } | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!jobUuid && !consultJobId) return;

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const result = jobUuid
          ? await fetchSessionByJobUuid(jobUuid)
          : await fetchSessionByJobId(consultJobId!);
        if (!cancelled) {
          setSessionData(result);
          setPollError(false);
          if (result?.status === 'pending' || result?.status === 'processing') {
            pollRef.current = setTimeout(poll, 4000);
          }
        }
      } catch {
        if (!cancelled) setPollError(true);
      }
    };

    setIsLoading(true);
    (jobUuid
      ? fetchSessionByJobUuid(jobUuid)
      : fetchSessionByJobId(consultJobId!)
    )
      .then((result) => {
        if (!cancelled) {
          setSessionData(result);
          setIsLoading(false);
          if (result?.status === 'pending' || result?.status === 'processing') {
            pollRef.current = setTimeout(poll, 4000);
          }
        }
      })
      .catch(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [jobUuid, consultJobId]);

  const handleReview = useCallback(async () => {
    setReviewing(true);
    try {
      await reviewPatientAnalysis(plan.id, activeLog.id, practitionerId);
      onComplete();
    } catch {
      showError('Failed to mark analysis as reviewed');
    } finally {
      setReviewing(false);
    }
  }, [plan.id, activeLog.id, practitionerId, onComplete, showError]);

  const jobStatus = sessionData?.status;
  const session = sessionData?.session ?? null;
  const isGenerating = isLoading || (!pollError && (jobStatus === 'pending' || jobStatus === 'processing'));
  const hasFailed = jobStatus === 'failed';

  if (pollError) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 24 }}>
        <Alert
          type="warning"
          message="Unable to check analysis status"
          description="A network error occurred while polling for results. The analysis may still be running."
          style={{ width: '100%', fontSize: 12 }}
        />
        <Button
          size="small"
          onClick={() => {
            setPollError(false);
            pollRef.current = setTimeout(async () => {
              try {
                const result = jobUuid
                  ? await fetchSessionByJobUuid(jobUuid!)
                  : await fetchSessionByJobId(consultJobId!);
                setSessionData(result);
                setPollError(false);
              } catch {
                setPollError(true);
              }
            }, 0);
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (isGenerating) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 280,
          gap: 20,
        }}
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: 'var(--mg-primary)' }} />} />
        <div style={{ textAlign: 'center' }}>
          <Text strong style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>
            Generating Patient Analysis
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Analysing patient profile, genetic data, and medical history…
          </Text>
        </div>
        {jobStatus === 'processing' && (
          <Progress percent={undefined} status="active" style={{ width: 260 }} />
        )}
      </div>
    );
  }

  if (hasFailed) {
    return (
      <Result
        status="error"
        title="Analysis Generation Failed"
        subTitle="The AI analysis could not be completed. You can retry or skip this step."
      />
    );
  }

  if (!session) {
    return (
      <Alert
        type="warning"
        message="Analysis not yet available"
        description="The guidance session has not been created yet. Please wait a moment and try again."
        style={{ fontSize: 12 }}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ConsultGuidancePanel
        sessionId={(session as { id: number }).id}
        appointmentId={null as unknown as number}
        initialSession={session}
      />
      {isStageComplete(activeLog) ? (
        <StageStatusNote log={activeLog} />
      ) : (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={reviewing}
            disabled={hasFailed || !session}
            onClick={handleReview}
            style={{ height: 32, fontSize: 12 }}
          >
            Review Complete — Continue Plan
          </Button>
        </div>
      )}
    </div>
  );
};

// ── Awaiting blood results renderer ────────────────────────────────────────

interface AwaitingBloodResultsRendererProps {
  plan: PatientTreatmentPlan;
  activeLog: PatientTreatmentPlanStageLog;
  requiredTests: string[];
  onComplete: () => void;
}

const AwaitingBloodResultsRenderer: React.FC<AwaitingBloodResultsRendererProps> = ({
  plan,
  activeLog,
  requiredTests,
  onComplete,
}) => {
  useNotifications();
  const [checking, setChecking] = useState(false);
  const [missingTests, setMissingTests] = useState<string[] | null>(null);

  const handleCheck = async () => {
    setChecking(true);
    setMissingTests(null);
    try {
      await checkBloodResults(plan.id, activeLog.id);
      onComplete();
    } catch (err: any) {
      const missing: string[] = err?.response?.data?.missing ?? [];
      setMissingTests(missing.length > 0 ? missing : requiredTests);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid var(--mg-border)', background: 'var(--mg-bg-subtle)' }}>
        <Text style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 6, color: 'var(--mg-text)' }}>
          Required Blood Tests
        </Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {requiredTests.map((t) => (
            <Alert
              key={t}
              message={t.replace(/_/g, ' ')}
              type="warning"
              showIcon={false}
              style={{ padding: '2px 8px', fontSize: 11, marginBottom: 0, border: '1px solid #faad14' }}
            />
          ))}
        </div>
      </div>

      {missingTests && missingTests.length > 0 && (
        <Alert
          type="error"
          message="Missing blood test results"
          description={
            <span style={{ fontSize: 11 }}>
              The following required tests were not found in recent results:{' '}
              <strong>{missingTests.map((t) => t.replace(/_/g, ' ')).join(', ')}</strong>.
              Please upload the results and try again.
            </span>
          }
        />
      )}

      {isStageComplete(activeLog) ? (
        <StageStatusNote log={activeLog} />
      ) : (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="primary" size="small" loading={checking} onClick={handleCheck}>
            Check Results Now
          </Button>
        </div>
      )}
    </div>
  );
};

// ── Sub-plan selection renderer ─────────────────────────────────────────────

interface SubPlanSelectionRendererProps {
  plan: PatientTreatmentPlan;
  activeLog: PatientTreatmentPlanStageLog;
  practitionerId: UserUuid;
  onComplete: () => void;
}

const SubPlanSelectionRenderer: React.FC<SubPlanSelectionRendererProps> = ({
  plan,
  activeLog,
  practitionerId,
  onComplete,
}) => {
  const { showError } = useNotifications();
  const options: SubPlanOption[] = activeLog.outcome_data?.available_options ?? [];
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Already assigned — the selection is recorded; show a read-only confirmation (historic view).
  if (isStageComplete(activeLog)) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={SECTION_LABEL_STYLE}>Select Sub-Plans to Assign</div>
        <StageStatusNote log={activeLog} />
      </div>
    );
  }

  const handleAssign = async () => {
    if (selectedTemplateIds.length === 0) return;
    setSubmitting(true);
    try {
      await selectSubPlans(plan.id, activeLog.id, selectedTemplateIds, practitionerId);
      onComplete();
    } catch {
      showError('Failed to assign sub-plans');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={SECTION_LABEL_STYLE}>Select Sub-Plans to Assign</div>
      <Select
        mode="multiple"
        placeholder="Choose plan(s) to assign…"
        style={{ width: '100%' }}
        value={selectedTemplateIds}
        onChange={setSelectedTemplateIds}
        options={options.map((o) => ({ value: o.template_id, label: o.template_name }))}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Space>
          <Button
            type="primary"
            icon={<ApartmentOutlined />}
            loading={submitting}
            disabled={selectedTemplateIds.length === 0}
            onClick={handleAssign}
            style={{ height: 32, fontSize: 12 }}
          >
            Assign Plans
          </Button>
        </Space>
      </div>
    </div>
  );
};

// ── Practitioner option (decision point) renderer ───────────────────────────

interface PractitionerOptionRendererProps {
  plan: PatientTreatmentPlan;
  activeLog: PatientTreatmentPlanStageLog;
  practitionerId: UserUuid;
  onComplete: () => void;
}

const PractitionerOptionRenderer: React.FC<PractitionerOptionRendererProps> = ({
  plan,
  activeLog,
  practitionerId,
  onComplete,
}) => {
  const { showError } = useNotifications();
  const prompt = activeLog.outcome_data?.prompt as string | undefined;
  const options: PractitionerOption[] = activeLog.outcome_data?.options ?? [];
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Already decided — show the recorded choice read-only (historic view).
  if (isStageComplete(activeLog)) {
    const chosenLabel =
      (activeLog.outcome_data?.selected_option_label as string | undefined) ??
      options.find((o) => `option_${o.option_key}` === activeLog.outcome)?.label ??
      'Decision recorded';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {prompt && <div style={SECTION_LABEL_STYLE}>{prompt}</div>}
        <Space>
          <CheckCircleOutlined style={{ color: 'var(--mg-success, #52c41a)' }} />
          <Text style={{ fontSize: 12 }}>Decision: {chosenLabel}</Text>
        </Space>
      </div>
    );
  }

  const handleConfirm = async () => {
    if (!selectedKey) return;
    setSubmitting(true);
    try {
      await advanceStage(plan.id, activeLog.id, undefined, undefined, practitionerId, selectedKey);
      onComplete();
    } catch {
      showError('Failed to record decision');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={SECTION_LABEL_STYLE}>{prompt || 'Select an option to continue'}</div>
      <Radio.Group
        value={selectedKey}
        onChange={(e) => setSelectedKey(e.target.value)}
        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        {options.map((opt) => (
          <Radio key={opt.option_key} value={opt.option_key} style={{ alignItems: 'flex-start' }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>{opt.label}</span>
            {opt.description && (
              <div style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>
                {opt.description}
              </div>
            )}
          </Radio>
        ))}
      </Radio.Group>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Space>
          <Button
            type="primary"
            icon={<CaretRightOutlined />}
            loading={submitting}
            disabled={!selectedKey}
            onClick={handleConfirm}
            style={{ height: 32, fontSize: 12 }}
          >
            Continue
          </Button>
        </Space>
      </div>
    </div>
  );
};

// ── Watcher status renderer ─────────────────────────────────────────────────

interface WatcherItem {
  item_key: string;
  label: string;
  watcher_type: string;
  watch_config: Record<string, any>;
  is_triggered: boolean;
  triggered_at: string | null;
}

function watcherItemDescription(item: WatcherItem): string {
  const cfg = item.watch_config ?? {};
  if (cfg.form_name)             return cfg.form_name;
  if (cfg.appointment_type_name) return cfg.appointment_type_name;
  if (cfg.medication_name)       return cfg.medication_name;
  if (cfg.medication_category)   return cfg.medication_category;
  return item.label;
}

/** Form_completed targets on a watcher, with whether each has been submitted yet. */
function getFormWatchItems(
  log: PatientTreatmentPlanStageLog,
): { formId: number; name: string; satisfied: boolean }[] {
  const items = (log.watcher?.watcher_items ?? []) as WatcherItem[];
  if (items.length > 0) {
    return items
      .filter((i) => i.watcher_type === 'form_completed' && i.watch_config?.form_id)
      .map((i) => ({
        formId: i.watch_config.form_id as number,
        name: (i.watch_config.form_name as string) ?? i.label ?? 'Form',
        satisfied: !!i.is_triggered,
      }));
  }
  const wc = log.watcher?.watch_config ?? {};
  const type = log.watcher?.watcher_type ?? (log.outcome_data?.watching as string | undefined);
  if (type === 'form_completed' && wc.form_id) {
    return [
      {
        formId: wc.form_id as number,
        name: (wc.form_name as string) ?? 'Form',
        satisfied: !!log.watcher?.triggered_at || log.status === 'completed',
      },
    ];
  }
  return [];
}

interface WatcherStatusPanelProps {
  log: PatientTreatmentPlanStageLog;
  onViewForm?: (formId: number) => void;
}

/**
 * Presentational watcher state — what it's watching for, current progress, and (for a satisfied
 * form_completed watcher) a button to open the submitted form. Reused both for a standalone watcher
 * stage and for a watcher joined to an action (the action view).
 */
const WatcherStatusPanel: React.FC<WatcherStatusPanelProps> = ({ log, onViewForm }) => {
  const outcomeData    = log.outcome_data ?? {};
  const watching       = outcomeData.watching as string | undefined;
  const watcherItems   = (log.watcher?.watcher_items ?? []) as WatcherItem[];
  const totalItems     = outcomeData.total_items as number | undefined;
  const triggeredCount = outcomeData.triggered_items as number | undefined;
  const satisfied      = log.status === 'completed';

  const formItems = getFormWatchItems(log);

  // Patient-centric wording: describe what the patient still needs to do, not "conditions".
  const types = watcherItems.length > 0
    ? watcherItems.map((i) => i.watcher_type)
    : [log.watcher?.watcher_type ?? watching ?? ''];
  const allForms = types.length > 0 && types.every((t) => t === 'form_completed');
  const count = totalItems ?? (watcherItems.length || 1);
  const doneCount = triggeredCount ?? watcherItems.filter((i) => i.is_triggered).length;
  const noun = allForms
    ? (count > 1 ? 'these forms' : 'this form')
    : (count > 1 ? 'these tasks' : 'this task');
  const singleName =
    watching !== 'multi' && watcherItems.length === 0
      ? ((log.watcher?.watch_config?.form_name as string | undefined) ?? formItems[0]?.name)
      : undefined;

  const headline = satisfied
    ? (allForms
        ? `The patient has completed ${count > 1 ? 'the requested forms' : 'the requested form'}`
        : 'The patient has completed the requested tasks')
    : `Waiting for the patient to complete ${noun}${singleName ? `: ${singleName}` : ''}${count > 1 ? ` (${doneCount} of ${count} done)` : ''}`;

  const accent = satisfied
    ? { border: 'var(--mg-success, #52c41a)', bg: 'var(--mg-success-bg, #f6ffed)', text: 'var(--mg-success, #389e0d)' }
    : { border: 'var(--mg-warning-border, #faad14)', bg: 'var(--mg-warning-bg, #fffbe6)', text: 'var(--mg-warning-text, #ad6800)' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        style={{
          padding: '10px 12px',
          borderRadius: 6,
          border: `1px solid ${accent.border}`,
          background: accent.bg,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: watching === 'multi' && watcherItems.length > 0 ? 10 : 0 }}>
          {satisfied
            ? <CheckCircleOutlined style={{ color: accent.text, fontSize: 13 }} />
            : <ClockCircleOutlined style={{ color: accent.text, fontSize: 13 }} />}
          <Text style={{ fontSize: 12, fontWeight: 600, color: accent.text }}>
            {headline}
          </Text>
        </div>

        {watching === 'multi' && watcherItems.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {watcherItems.map((item) => {
              const description = watcherItemDescription(item);
              return (
                <div key={item.item_key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                  <div style={{ flexShrink: 0, display: 'flex' }}>
                    {item.is_triggered ? (
                      <CheckCircleOutlined style={{ color: 'var(--mg-success, #52c41a)', fontSize: 13 }} />
                    ) : (
                      <ClockCircleOutlined style={{ color: 'var(--mg-text-disabled)', fontSize: 13 }} />
                    )}
                  </div>
                  <Text
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 12,
                      color: item.is_triggered ? 'var(--mg-text-secondary)' : 'var(--mg-text)',
                      textDecoration: item.is_triggered ? 'line-through' : 'none',
                    }}
                  >
                    {description}
                  </Text>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* A submitted form can be opened read-only directly from here. */}
      {onViewForm && formItems.filter((f) => f.satisfied).map((f) => (
        <Button
          key={f.formId}
          size="small"
          icon={<FileTextOutlined />}
          onClick={() => onViewForm(f.formId)}
          style={{ alignSelf: 'flex-start', fontSize: 12 }}
        >
          View completed form{f.name ? `: ${f.name}` : ''}
        </Button>
      ))}

      {!satisfied && (
        <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)', textAlign: 'center' as const }}>
          This step will advance automatically when all conditions are met.
        </Text>
      )}
    </div>
  );
};

interface WatcherStatusRendererProps {
  activeLog: PatientTreatmentPlanStageLog;
  onViewForm?: (formId: number) => void;
}

const WatcherStatusRenderer: React.FC<WatcherStatusRendererProps> = ({ activeLog, onViewForm }) => (
  <WatcherStatusPanel log={activeLog} onViewForm={onViewForm} />
);

// ── Action + joined-watcher view ────────────────────────────────────────────
// Shown when the plan has reached a watcher joined to an action: lists every action that was
// actually performed (e.g. form assigned, notification sent) and what the patient now needs to do.

const PerformedActionRow: React.FC<{ action: PerformedActionVM }> = ({ action }) => {
  const status = action.status.toLowerCase();
  const skipped = status === 'skipped';
  const failed = status === 'failed' || status === 'error';
  const icon = skipped
    ? <MinusCircleOutlined style={{ color: 'var(--mg-text-disabled)', fontSize: 14 }} />
    : failed
      ? <CloseCircleOutlined style={{ color: 'var(--mg-danger, #ff4d4f)', fontSize: 14 }} />
      : <CheckCircleOutlined style={{ color: 'var(--mg-success, #52c41a)', fontSize: 14 }} />;

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <div style={{ paddingTop: 1, flexShrink: 0, display: 'flex' }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ fontSize: 12, color: skipped ? 'var(--mg-text-secondary)' : 'var(--mg-text)' }}>
          {action.label}
          {action.name ? ': ' : ''}
          {action.name && <Text strong style={{ fontSize: 12 }}>{action.name}</Text>}
        </Text>
        {skipped && (
          <Text style={{ display: 'block', fontSize: 11, color: 'var(--mg-text-secondary)' }}>
            Skipped{action.reason ? ` — ${action.reason}` : ''}
          </Text>
        )}
      </div>
    </div>
  );
};

interface ActionWithWatcherViewProps {
  actionLog: PatientTreatmentPlanStageLog;
  actionNode: FlowNode | undefined;
  watcherLog: PatientTreatmentPlanStageLog;
  onViewForm?: (formId: number) => void;
}

const ActionWithWatcherView: React.FC<ActionWithWatcherViewProps> = ({
  actionLog,
  actionNode,
  watcherLog,
  onViewForm,
}) => {
  const stageType = (actionLog.stage_snapshot?.stage_type ?? actionNode?.data?.stage_type) as StageType | undefined;
  const taskType = (actionLog.outcome_data?.task_type
    ?? actionNode?.data?.configuration?.task_type) as PractitionerTaskType | undefined;
  const title =
    actionLog.outcome_data?.task_title
    ?? actionLog.stage_snapshot?.title
    ?? actionNode?.data?.label
    ?? (stageType ? STAGE_CONFIG[stageType]?.label : 'Action');

  const performedActions = buildPerformedActions(actionLog);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {stageType && getStageIcon(stageType, 16, taskType)}
        <Text strong style={{ fontSize: 13 }}>{title}</Text>
      </div>

      {performedActions.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '10px 12px',
            borderRadius: 6,
            border: '1px solid var(--mg-border)',
            background: 'var(--mg-bg-subtle)',
          }}
        >
          {performedActions.map((a) => (
            <PerformedActionRow key={a.key} action={a} />
          ))}
        </div>
      )}

      <WatcherStatusPanel log={watcherLog} onViewForm={onViewForm} />
    </div>
  );
};

// ── Advance Stage renderer ──────────────────────────────────────────────────

interface AdvanceStageRendererProps {
  plan: PatientTreatmentPlan;
  activeLog: PatientTreatmentPlanStageLog;
  practitionerId: UserUuid;
  onComplete: () => void;
}

const AdvanceStageRenderer: React.FC<AdvanceStageRendererProps> = ({
  plan,
  activeLog,
  practitionerId,
  onComplete,
}) => {
  const { showError } = useNotifications();
  const [advancing, setAdvancing] = useState(false);

  const handleAdvance = async () => {
    setAdvancing(true);
    try {
      await advanceStage(plan.id, activeLog.id, undefined, undefined, practitionerId);
      onComplete();
    } catch {
      showError('Failed to advance stage');
    } finally {
      setAdvancing(false);
    }
  };

  if (isStageComplete(activeLog)) {
    return <StageStatusNote log={activeLog} />;
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8 }}>
      <Button
        type="primary"
        ghost
        icon={<CaretRightOutlined />}
        loading={advancing}
        onClick={handleAdvance}
        style={{ height: 32, fontSize: 12 }}
      >
        Advance Stage
      </Button>
    </div>
  );
};

// ── Admin Review renderer (read-only) ───────────────────────────────────────
// admin_review freezes the plan until a MyGene admin approves it. This approval CANNOT happen in the
// clinical app — the clinic only ever displays the frozen state. Render NO approve / reject / advance
// buttons here.

interface AdminReviewRendererProps {
  activeLog: PatientTreatmentPlanStageLog;
}

const AdminReviewRenderer: React.FC<AdminReviewRendererProps> = ({ activeLog }) => {
  const outcomeData = activeLog.outcome_data ?? {};
  const wasReturned = outcomeData.status === 'rejected' && !!outcomeData.review_notes;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 16px',
          borderRadius: 8,
          background: 'var(--mg-warning-bg)',
          border: '1px solid var(--mg-warning)',
        }}
      >
        <SafetyCertificateOutlined style={{ color: 'var(--mg-warning)', fontSize: 18 }} />
        <Text style={{ fontSize: 12, fontWeight: 500, color: 'var(--mg-warning-text, var(--mg-warning))' }}>
          Plan frozen — awaiting Xoe Med admin review.
        </Text>
      </div>

      {wasReturned && (
        <Alert
          type="warning"
          message="Review returned with notes"
          description={<span style={{ fontSize: 12 }}>{outcomeData.review_notes as string}</span>}
          style={{ fontSize: 12 }}
        />
      )}
    </div>
  );
};

// ── Main InlineStageRenderer ────────────────────────────────────────────────

const InlineStageContent: React.FC<InlineStageRendererProps> = ({
  plan,
  activeLog,
  activeNode,
  joinedWatcher,
  onViewForm,
  patientId,
  patientUuid,
  practitionerId,
  userId,
  onComplete,
  onPlanChange,
  stageLogs = [],
}) => {
  const { showError } = useNotifications();
  const [skipping, setSkipping] = useState(false);

  // When a watcher is joined to this stage and the plan has reached it, present the merged
  // action + watcher view rather than routing the (completed) action through its own renderer.
  const joinedWatcherLog = joinedWatcher?.log ?? null;
  if (joinedWatcherLog) {
    // The CoA-analysis watcher merges onto the CoA upload task. Don't show the generic
    // "patient completed the tasks" merged view there — show the AI analysis result (read from
    // the upload log, which carries ai_coa_analysis; falls back to the latest across the plan).
    if (isCoaAnalysisWatcher(joinedWatcherLog)) {
      // Scope the CoA lookup to THIS attempt's loop_iteration so a fresh attempt-2 shows the pending
      // state rather than attempt-1's analysed result.
      const analysis =
        (activeLog.outcome_data?.ai_coa_analysis as CoaAnalysis | undefined)
        ?? latestCoaAnalysis(stageLogs, activeLog.loop_iteration);
      // Same rule as `analysis` above: this log's own file ids first, else the matching upload
      // log for this attempt — never a different iteration's files.
      const coaFileIds = (activeLog.outcome_data?.ai_coa_analysis
        ? (activeLog.outcome_data?.task_result?.uploaded_file_ids as string[] | undefined)
        : coaFileIdsFromLatestUpload(stageLogs, activeLog.loop_iteration));
      return (
        <CoaAnalysisPanel
          analysis={analysis}
          hasCoaFile
          fileIds={coaFileIds}
          patientId={patientId}
        />
      );
    }
    return (
      <ActionWithWatcherView
        actionLog={activeLog}
        actionNode={activeNode}
        watcherLog={joinedWatcherLog}
        onViewForm={onViewForm}
      />
    );
  }

  const awaitingAction = activeLog.outcome_data?.awaiting_action as string | undefined;
  const stageType = (
    activeLog.stage_snapshot?.stage_type ?? activeNode?.data?.stage_type
  ) as StageType | undefined;
  const isRequired = activeLog.stage_snapshot?.is_required ?? true;

  const handleComplete = useCallback(() => {
    onComplete();
    onPlanChange();
  }, [onComplete, onPlanChange]);

  const handleSkip = () => {
    Modal.confirm({
      title: 'Skip this step?',
      content: 'This step is optional. Are you sure you want to skip it?',
      okText: 'Skip',
      cancelText: 'Cancel',
      onOk: async () => {
        setSkipping(true);
        try {
          await skipStage(plan.id, activeLog.id, practitionerId);
          handleComplete();
        } catch {
          showError('Failed to skip stage');
        } finally {
          setSkipping(false);
        }
      },
    });
  };

  const withSkipButton = (content: React.ReactNode) => (
    <>
      {content}
      {!isRequired && !isStageComplete(activeLog) && (
        <div style={{ marginTop: 8 }}>
          <div style={{ marginBottom: 8, padding: '8px 12px', backgroundColor: '#fff7e6', borderLeft: '3px solid #faad14', borderRadius: '2px' }}>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--mg-text-secondary)', lineHeight: '1.5' }}>
              <strong>⚠️ Recommended:</strong> It is recommended to complete this action for complete compliance. However, this section can be skipped at the doctor's discretion.
            </p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="link"
              size="small"
              loading={skipping}
              onClick={handleSkip}
              style={{ color: 'var(--mg-text-secondary)', fontSize: 11 }}
            >
              Skip this step
            </Button>
          </div>
        </div>
      )}
    </>
  );

  // Route by awaiting_action first, then fall back to stage_type
  if (
    awaitingAction === 'practitioner_task' ||
    (!awaitingAction && stageType === 'practitioner_task')
  ) {
    return withSkipButton(
      <PractitionerTaskRenderer
        plan={plan}
        activeLog={activeLog}
        patientId={patientId}
        patientUuid={patientUuid}
        practitionerId={practitionerId}
        userId={userId}
        onComplete={handleComplete}
      />
    );
  }

  if (awaitingAction === 'awaiting_blood_results') {
    const requiredTests: string[] = activeLog.outcome_data?.required_blood_tests ?? [];
    return withSkipButton(
      <AwaitingBloodResultsRenderer
        plan={plan}
        activeLog={activeLog}
        requiredTests={requiredTests}
        onComplete={handleComplete}
      />
    );
  }

  if (
    awaitingAction === 'patient_analysis_review' ||
    (!awaitingAction && stageType === 'patient_analysis')
  ) {
    return withSkipButton(
      <PatientAnalysisRenderer
        plan={plan}
        activeLog={activeLog}
        practitionerId={practitionerId}
        onComplete={handleComplete}
      />
    );
  }

  if (awaitingAction === 'comprehensive_analysis_input') {
    return withSkipButton(
      <ComprehensiveAnalysisInputRenderer
        plan={plan}
        activeLog={activeLog}
        onComplete={handleComplete}
      />
    );
  }

  if (
    awaitingAction === 'comprehensive_analysis_review' ||
    (!awaitingAction && stageType === 'comprehensive_analysis')
  ) {
    // Comprehensive analysis review is never skippable mid-review — skip not shown here
    return (
      <ComprehensiveAnalysisRenderer
        analysisId={activeLog.outcome_data?.analysis_id as number}
        planId={plan.id}
        onReviewed={handleComplete}
        patientGender={plan.patient?.sex || plan.patient?.gender}
      />
    );
  }

  if (awaitingAction === 'sub_plan_selection') {
    return withSkipButton(
      <SubPlanSelectionRenderer
        plan={plan}
        activeLog={activeLog}
        practitionerId={practitionerId}
        onComplete={handleComplete}
      />
    );
  }

  if (
    awaitingAction === 'practitioner_option' ||
    (!awaitingAction && stageType === 'practitioner_option')
  ) {
    return withSkipButton(
      <PractitionerOptionRenderer
        plan={plan}
        activeLog={activeLog}
        practitionerId={practitionerId}
        onComplete={handleComplete}
      />
    );
  }

  if (
    awaitingAction === 'admin_review' ||
    (!awaitingAction && stageType === 'admin_review')
  ) {
    // Read-only: the clinic can only display the frozen state — no approve / reject / advance / skip.
    return <AdminReviewRenderer activeLog={activeLog} />;
  }

  if (!awaitingAction && stageType === 'watcher') {
    // The CoA-analysis watcher is not waiting on the patient — it is waiting on the AI analysis.
    // Show the analysis segment instead of the generic "waiting for the patient" message: the
    // in-progress state while the job runs, and the full result (read from the upload stage-log)
    // once it lands / when viewing the watcher after the task.
    if (isCoaAnalysisWatcher(activeLog)) {
      // Scope to this attempt's loop_iteration — a fresh attempt shows pending, not a prior result.
      return withSkipButton(
        <CoaAnalysisPanel
          analysis={latestCoaAnalysis(stageLogs, activeLog.loop_iteration)}
          hasCoaFile
          fileIds={coaFileIdsFromLatestUpload(stageLogs, activeLog.loop_iteration)}
          patientId={patientId}
        />
      );
    }
    return withSkipButton(<WatcherStatusRenderer activeLog={activeLog} onViewForm={onViewForm} />);
  }

  // Time-delay stage the plan is parked on — show the waiting state + when it resumes,
  // rather than a generic "Advance Stage" button (the delay fires automatically).
  if (!awaitingAction && stageType === 'time_condition') {
    return <TimeDelayStatusRenderer log={activeLog} />;
  }

  // Default: advance/input stages
  return withSkipButton(
    <AdvanceStageRenderer
      plan={plan}
      activeLog={activeLog}
      practitionerId={practitionerId}
      onComplete={handleComplete}
    />
  );
};

/**
 * Wraps the stage content (which renders the comprehensive analysis and AI-filled
 * forms) in the evidence-ref interaction provider so that clicking any reference shown
 * on a piece of generated info opens that record's detail modal. The shared
 * EvidenceRefList reads this context, so no new props are threaded through the
 * analysis/form/renderer components. `{modals}` is rendered once alongside.
 */
const InlineStageRenderer: React.FC<InlineStageRendererProps> = (props) => {
  const { openRef, modals } = useReferenceModal(props.patientId);

  return (
    <EvidenceRefInteractionContext.Provider value={{ onRefClick: openRef }}>
      <InlineStageContent {...props} />
      {modals}
    </EvidenceRefInteractionContext.Provider>
  );
};

export default InlineStageRenderer;
